import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Eye,
  ExternalLink,
  Loader2,
  Pencil,
  RotateCcw,
  Trash2,
} from "lucide-react";

import { PAGES, migrateBlocks } from "@/cms/blockRegistry";
import DEFAULT_CONTENT from "@/cms/defaultContent";
import PagePicker from "@/cms/admin/PagePicker";
import BlockList from "@/cms/admin/BlockList";
import PreviewPane from "@/cms/admin/PreviewPane";
import StringsEditor, { seedStringValues } from "@/cms/admin/StringsEditor";

// Not a block-based page like the rest of PAGES — see its comment in
// blockRegistry.js.
const STRINGS_PAGE_KEY = "site-text";

/**
 * @param {{
 *   onFetchPageContent: (pageKey: string) => Promise<any>;
 *   onSaveDraft: (pageKey: string, blocks: any[]) => Promise<void>;
 *   onPublish: (pageKey: string, blocks: any[]) => Promise<void>;
 *   onUploadImage: (pageKey: string, file: File) => Promise<string>;
 *   onListCustomPages: () => Promise<{slug: string, title: string}[]>;
 *   onCreateCustomPage: (slug: string, title: string) => Promise<void>;
 *   onDeleteCustomPage: (slug: string) => Promise<void>;
 *   onFetchStrings: () => Promise<any>;
 *   onSaveStringsDraft: (values: Record<string, string>) => Promise<void>;
 *   onPublishStrings: (values: Record<string, string>) => Promise<void>;
 * }} props
 */
export default function EditSiteAdmin({
  onFetchPageContent,
  onSaveDraft,
  onPublish,
  onUploadImage,
  onListCustomPages,
  onCreateCustomPage,
  onDeleteCustomPage,
  onFetchStrings, 
  onSaveStringsDraft,
  onPublishStrings,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageKey = searchParams.get("page");
  const isStringsPage = pageKey === STRINGS_PAGE_KEY;

  const [customPages, setCustomPages] = useState([]);
  const [loadingCustomPages, setLoadingCustomPages] = useState(true);

  const refreshCustomPages = useCallback(async () => {
    setLoadingCustomPages(true);
    try {
      setCustomPages(await onListCustomPages());
    } finally {
      setLoadingCustomPages(false);
    }
  }, [onListCustomPages]);

  useEffect(() => {
    refreshCustomPages();
  }, [refreshCustomPages]);

  const customPage = !pageKey
    ? null
    : customPages.find((p) => p.slug === pageKey) || null;
  // Fixed pages come from the static registry; anything else editable
  // here is a custom page, whose "label" is whatever title the admin gave
  // it when creating it (or the slug itself while that list is still
  // loading, so the header isn't blank).
  const pageMeta = pageKey
    ? PAGES[pageKey] || (customPage ? { label: customPage.title } : { label: pageKey })
    : null;
  const isCustomPage = !!pageKey && !PAGES[pageKey];

  const [loading, setLoading] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [publishedBlocks, setPublishedBlocks] = useState(null);
  const [tab, setTab] = useState("edit"); // "edit" | "preview"
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Text & Labels is a flat key/value editor (StringsEditor), not a
  // blocks array — its own loading/value/publish state, parallel to (not
  // sharing) the block-editor state above.
  const [stringValues, setStringValues] = useState({});
  const [publishedStringValues, setPublishedStringValues] = useState(null);

  useEffect(() => {
    if (!isStringsPage) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const data = await onFetchStrings();
        const draft = data?.draft;
        const published = data?.published;
        if (cancelled) return;
        setStringValues(seedStringValues(draft || published));
        setPublishedStringValues(seedStringValues(published));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isStringsPage, onFetchStrings]);

  useEffect(() => {
    if (!pageKey || isStringsPage) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const data = await onFetchPageContent(pageKey);
        const fallback = DEFAULT_CONTENT[pageKey] || [];
        const draft = data?.draft?.blocks;
        const published = data?.published?.blocks;
        if (cancelled) return;
        setBlocks(
          migrateBlocks(
            Array.isArray(draft) && draft.length > 0
              ? draft
              : Array.isArray(published) && published.length > 0
                ? published
                : fallback,
          ),
        );
        setPublishedBlocks(
          migrateBlocks(
            Array.isArray(published) && published.length > 0
              ? published
              : fallback,
          ),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pageKey, isStringsPage, onFetchPageContent]);

  const selectPage = useCallback(
    (key) => setSearchParams({ page: key }),
    [setSearchParams],
  );
  const backToPicker = useCallback(() => setSearchParams({}), [setSearchParams]);

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      if (isStringsPage) {
        await onSaveStringsDraft(stringValues);
      } else {
        await onSaveDraft(pageKey, blocks);
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      if (isStringsPage) {
        await onPublishStrings(stringValues);
        setPublishedStringValues(stringValues);
      } else {
        await onPublish(pageKey, blocks);
        setPublishedBlocks(blocks);
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleDiscard = () => {
    if (isStringsPage) {
      if (publishedStringValues) setStringValues(publishedStringValues);
    } else if (publishedBlocks) {
      setBlocks(publishedBlocks);
    }
  };

  // Optimistic local updates so the picker's list is right immediately —
  // no round-trip refetch needed before the new/removed page shows up.
  const handleCreatePage = async (slug, title) => {
    await onCreateCustomPage(slug, title);
    setCustomPages((prev) => [{ slug, title }, ...prev]);
  };

  const handleDeletePage = async (slug) => {
    await onDeleteCustomPage(slug);
    setCustomPages((prev) => prev.filter((p) => p.slug !== slug));
    if (pageKey === slug) backToPicker();
  };

  const handleDeleteCurrentPage = async () => {
    if (
      !window.confirm(
        "Delete this page? It will disappear from the site and its URL will 404.",
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      await handleDeletePage(pageKey);
    } finally {
      setDeleting(false);
    }
  };

  if (!pageKey || !pageMeta) {
    return (
      <div className="min-h-screen bg-[#F7F8F7] py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-[#0B2421]">Edit Site</h1>
            <p className="text-gray-500 mt-1">
              Choose a page to edit its content, then publish when you're
              ready to go live.
            </p>
          </motion.div>
          <PagePicker
            onSelect={selectPage}
            customPages={customPages}
            loadingCustomPages={loadingCustomPages}
            onCreatePage={handleCreatePage}
            onDeletePage={handleDeletePage}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8F7] py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={backToPicker}
                className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <h1 className="text-3xl font-bold text-[#0B2421]">
                {pageMeta.label}
              </h1>
              {isCustomPage && (
                <a
                  href={`/page/${pageKey}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-[#0B2421]"
                >
                  /page/{pageKey}
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>

            <div className="flex items-center gap-2">
              {isCustomPage && (
                <button
                  type="button"
                  onClick={handleDeleteCurrentPage}
                  disabled={deleting}
                  className="h-11 px-4 rounded-xl border border-red-200 text-red-500 font-medium hover:bg-red-50 disabled:opacity-40 flex items-center gap-1.5 text-sm"
                >
                  {deleting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                  Delete Page
                </button>
              )}
              <button
                type="button"
                onClick={handleDiscard}
                disabled={isStringsPage ? !publishedStringValues : !publishedBlocks}
                className="h-11 px-4 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1.5 text-sm"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Discard changes
              </button>
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={saving || publishing}
                className="h-11 px-5 rounded-xl border border-[#0B2421]/20 text-[#0B2421] font-semibold hover:bg-[#0B2421]/5 disabled:opacity-40 flex items-center gap-1.5 text-sm"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save Draft
              </button>
              <button
                type="button"
                onClick={handlePublish}
                disabled={saving || publishing}
                className="h-11 px-5 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold disabled:opacity-40 flex items-center gap-1.5 text-sm"
              >
                {publishing && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                Publish
              </button>
            </div>
          </div>

          <p className="text-gray-500 mt-1">
            Save Draft keeps your changes private; Publish makes them live
            on the public site.
          </p>

          {!isStringsPage && (
            <div className="mt-5 inline-flex rounded-xl border border-gray-200 bg-white p-1">
              <button
                type="button"
                onClick={() => setTab("edit")}
                className={`h-9 px-4 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                  tab === "edit"
                    ? "bg-[#0B2421] text-white"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </button>
              <button
                type="button"
                onClick={() => setTab("preview")}
                className={`h-9 px-4 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                  tab === "preview"
                    ? "bg-[#0B2421] text-white"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                <Eye className="h-3.5 w-3.5" />
                Preview
              </button>
            </div>
          )}
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-400 gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading...
          </div>
        ) : isStringsPage ? (
          <StringsEditor values={stringValues} onChange={setStringValues} />
        ) : tab === "edit" ? (
          <BlockList
            pageKey={pageKey}
            blocks={blocks}
            onChange={setBlocks}
            onUploadImage={(file) => onUploadImage(pageKey, file)}
          />
        ) : (
          <PreviewPane blocks={blocks} />
        )}
      </div>
    </div>
  );
}
